import React from 'react';
import './PriceHistoryPanel.css';
import { useGetPriceHistoryQuery } from 'store/services/admin/adminCardPricingApi';

interface PriceHistoryPanelProps {
  cardId: number;
  isManualOverride: boolean;
  onToggleOverride: (enabled: boolean) => void;
}

const PriceHistoryPanel: React.FC<PriceHistoryPanelProps> = ({
  cardId,
  isManualOverride,
  onToggleOverride,
}) => {
  const { data: history, isLoading, error } = useGetPriceHistoryQuery(cardId);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (value: number | null): string => {
    if (value === null || value === undefined) return '—';
    return `$${value.toFixed(2)}`;
  };

  const getFieldLabel = (field: string): string => {
    switch (field) {
      case 'usd':
        return 'Value';
      case 'usdFoil':
        return 'Foil Value';
      case 'usdEtched':
        return 'Etched Value';
      default:
        return field;
    }
  };

  return (
    <div className="price-history-panel">
      {/* Manual Override Section */}
      <div className="panel-section override-section">
        <div className="override-header">
          <h4 className="section-title">Manual Override</h4>
          <label className="toggle-container">
            <input
              type="checkbox"
              className="toggle-input"
              checked={isManualOverride}
              onChange={(e) => onToggleOverride(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <p className="override-description">
          {isManualOverride ? (
            <>
              <span className="status-enabled">Enabled</span> — Automated pricing scripts will{' '}
              <strong>NOT</strong> overwrite this price
            </>
          ) : (
            <>
              <span className="status-disabled">Disabled</span> — Automated pricing scripts may
              update this price
            </>
          )}
        </p>
      </div>

      {/* Price History Section */}
      {/* @ts-expect-error - React 19 types issue with children */}
      <div className="panel-section history-section">
        <h4 className="section-title">Price Change History</h4>

        {isLoading && <div className="history-loading">Loading history...</div>}

        {error && <div className="history-error">Failed to load price history</div>}

        {!isLoading && !error && (!history || history.length === 0) && (
          <div className="history-empty">No price changes recorded</div>
        )}

        {!isLoading && !error && history && history.length > 0 && (
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Field</th>
                  <th>Previous</th>
                  <th>New</th>
                  <th>Changed By</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry.id} className={entry.priceSource === 'ADMIN_MANUAL' ? 'manual-entry' : ''}>
                    <td className="col-date">{formatDate(entry.changedAt)}</td>
                    <td className="col-field">{getFieldLabel(entry.field)}</td>
                    <td className="col-price previous">{formatPrice(entry.previousValue)}</td>
                    <td className="col-price new">{formatPrice(entry.newValue)}</td>
                    <td className="col-changed-by">
                      {entry.priceSource === 'ADMIN_MANUAL' && (
                        <span className="source-badge manual">M</span>
                      )}
                      {entry.priceSource === 'SCRYFALL' && (
                        <span className="source-badge auto">S</span>
                      )}
                      <span className="changed-by-name">
                        {entry.changedBy?.username || 'Automated'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceHistoryPanel;
